import { useQueryClient } from "@tanstack/react-query"
import Link from "next/link"
import { useRouter } from "next/router"
import { type FC, type ReactNode, useContext, useEffect, useRef, useState } from "react"
import { useDetectClickOutside } from "react-detect-click-outside"

import {
  type BaseCommunityFragment,
  CommunityContentDocument,
  type CommunityContentQuery,
  type CommunityContentQueryVariables,
  UserActivityEventType,
} from "../generated/graphql"
import { graphQLClient } from "../graphql/client"
import { AppContext } from "../providers/AppProvider"
import { UserContext } from "../providers/UserProvider"
import { communityIconUrl } from "../utils/community"
import { isCheckoutPage } from "../utils/routing"
import { useTrackUserActivity } from "../utils/tracking"
import { getUrlFromCode, useCommunityCustomization } from "../utils/useCommunityCustomization"
import { useCommunityFromCode } from "../utils/useCommunityFromCode"
import { useWindowSize } from "../utils/useWindowSize"
import { Cart } from "./Cart"
import { AvatarPlaceholderIcon } from "./icons/AvatarPlaceholderIcon"
import { InstagramIcon } from "./icons/InstagramIcon"
import { MaskIcon, MaskIconV2 } from "./icons/MaskIcon"
import { PointIcon } from "./icons/PointIcon"
import { SoundCloudIcon } from "./icons/SoundCloudIcon"
import { Triangle } from "./icons/TriangleIcon"
import WERZLogoIcon from "./icons/WERZLogoIcon"
import { YoutubeIcon } from "./icons/YoutubeIcon"
import UbiqButton, { UbiqButtonSize, UbiqButtonType } from "./UbiqButton"

const isProductionEnvironment = process.env.NEXT_PUBLIC_ENVIRONMENT === "production"

enum HEADER_ACTIONS {
  COMMUNITY_SWITCH = "COMMUNITY_SWITCH",
  BURGER = "BURGER",
  LOGOUT = "LOGOUT",
  CART = "CART",
}

const COMMUNITIES_WITH_NEW_FUNCTIONALITY = ["kune", "noisey", "oskadd", "blockula", "radiobrus"]

const _checkCommunitiesWithNewFunctionality = (communityCode: string) =>
  COMMUNITIES_WITH_NEW_FUNCTIONALITY.includes(communityCode)

export const shouldHide = (code: string) => ["kbk"].includes(code)

const LogOut: FC<{ onClick?: () => void; className: string; avatarId: string }> = ({
  onClick,
  className,
  avatarId,
}) => {
  const { user, logout } = useContext(UserContext)
  const { clearCart } = useContext(AppContext)

  const closeDropdown = () => setLogoutButtonVisible(false)
  const [logoutButtonVisible, setLogoutButtonVisible] = useState(false)
  const ref = useDetectClickOutside({ onTriggered: closeDropdown })
  const { isDesktop } = useWindowSize()

  const customizationData = useCommunityCustomization()

  if (!customizationData) return null
  const { code: communityCode } = customizationData

  return (
    <div
      className={className}
      onClick={() => {
        onClick?.()
        !!user && setLogoutButtonVisible(!logoutButtonVisible)
      }}
      ref={ref}
    >
      {user?.avatar ? (
        <MaskIcon id={avatarId} src={user?.avatar} width={isDesktop ? 36 : 24} />
      ) : isDesktop ? (
        <MaskIcon
          id="avatar-placeholder"
          reactSvg={<AvatarPlaceholderIcon height={57} width={57} />}
          width={isDesktop ? 36 : 24}
        />
      ) : (
        <AvatarPlaceholderIcon />
      )}

      <div
        className={`absolute bg-white border z-[1000] border-[#232734] px-6 items-center justify-center w-[98px] h-[60px] sm:h-[42px] sm:w-[80px] -right-2 top-[40px] sm:top-[0px] sm:right-[-95px] sm:bottom-[-25px] ${
          logoutButtonVisible ? "flex" : "hidden"
        }`}
        style={{ boxShadow: "2px 2px 0px rgba(0, 0, 0, 0.48)" }}
      >
        <div className="h-[16px] w-[16px] border-t-[1px] border-l-[1px] border-black rotate-[45deg] bg-[#FFFFFF] absolute right-[13px] top-[-8.5px] sm:left-[-8px] sm:rotate-[-45deg] sm:top-[12px]" />
        <UbiqButton
          buttonType={UbiqButtonType.DARK}
          onClick={() => {
            clearCart(communityCode)
            logout()
          }}
          size={UbiqButtonSize.EXTRA_SMALL}
          styles="uppercase mt-[3px] !text-[11px]"
          title="log out"
        />
      </div>
    </div>
  )
}

export const ContentWrappingMenu: FC<{
  showContent: boolean
  children: ReactNode | ReactNode[]
}> = ({ showContent, children }) => {
  const { user, communities: allCommunities } = useContext(UserContext)
  // Filter out standalone pages (fleamarket) from sidebar
  const communities = allCommunities.filter((c) => c.code !== "fleamarket")
  const customizationData = useCommunityCustomization()
  const { community: currentCommunity } = useCommunityFromCode()
  const router = useRouter()
  const queryClient = useQueryClient()
  const track = useTrackUserActivity()

  const [isSwitchCommunity, setIsSwitchCommunity] = useState(false)
  const [isOverFlow, setIsOverFlow] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [hoverCode, setHoverCode] = useState("")
  const [hoverTooltipTop, setHoverTooltipTop] = useState<number | null>(null)
  const isMobileOverlayOpen = isSwitchCommunity || isMenuOpen

  const containerRef = useRef<HTMLDivElement>(null)

  const closeMobileMenus = () => {
    setIsSwitchCommunity(false)
    setIsMenuOpen(false)
  }

  const prefetchCommunityContent = async (community: BaseCommunityFragment) => {
    if (!community.code) return

    await Promise.allSettled([
      router.prefetch(getUrlFromCode(community.code)),
      queryClient.prefetchQuery(
        ["CommunityContent", { communityId: community.id }],
        () =>
          graphQLClient.request<CommunityContentQuery, CommunityContentQueryVariables>(
            CommunityContentDocument,
            { communityId: community.id },
          ),
        { staleTime: 30_000 },
      ),
    ])
  }

  const handleCommunityClick = async (community: BaseCommunityFragment) => {
    if (!community.code) return
    const targetUrl = getUrlFromCode(community.code)

    track({
      data: {
        currentCommunity: customizationData?.code,
        selectedCommunity: community.code,
      },
      type: UserActivityEventType.SwitchCommunityOpened,
    })

    await prefetchCommunityContent(community)
    closeMobileMenus()
    void router.push(targetUrl, undefined, { scroll: false }).catch(() => {
      // Route cancellations reject the push promise; they're non-fatal.
    })
  }

  const handleButtonClick = (action: HEADER_ACTIONS) => {
    switch (action) {
      case HEADER_ACTIONS.BURGER:
        setIsMenuOpen(!isMenuOpen)
        setIsSwitchCommunity(false)
        break
      case HEADER_ACTIONS.COMMUNITY_SWITCH:
        setIsSwitchCommunity(!isSwitchCommunity)
        setIsMenuOpen(false)
        break
      case HEADER_ACTIONS.LOGOUT:
        setIsSwitchCommunity(false)
        setIsMenuOpen(false)
        break
      case HEADER_ACTIONS.CART:
        setIsSwitchCommunity(false)
        setIsMenuOpen(false)
        break
    }
  }

  // This useEffect should to watch on overflow block.
  // Because we need to add border top and bottom when we have overflow side bar menu.
  useEffect(() => {
    const checkOverflow = () => {
      const container = containerRef.current
      if (container) setIsOverFlow(container.scrollHeight > container.clientHeight)
    }
    checkOverflow()
    window.addEventListener("resize", checkOverflow)

    return () => {
      window.removeEventListener("resize", checkOverflow)
    }
  }, [communities.length])

  if (!customizationData) return null
  const {
    url,
    code: communityCode,
    styles: communityStyles,
    socials: { instagramLink, soundcloudLink, youtubeLink },
  } = customizationData

  const commonLogoProps = communityStyles.global.alternativeLogo
    ? {
        src: communityStyles.global.alternativeLogo.src,
        styles: communityStyles.global.alternativeLogo.styles,
      }
    : {
        src: communityStyles.global.logo.src,
        styles: communityStyles.global.logo.styles,
      }
  // Keep the header stable while the community query refetches during page transitions.
  const headerCommunityIconSrc = currentCommunity
    ? communityIconUrl(currentCommunity)
    : commonLogoProps.src
  const headerCommunityName = currentCommunity?.name ?? customizationData.name

  const handleSocialClick = (social: string, socialURL: string) => {
    track({
      data: { social, socialURL },
      type: UserActivityEventType.SocialLinksClicked,
    })
    window.open(socialURL, "_blank")
  }

  const detectColor = (currentCode: string) => {
    if (hoverCode === currentCode && currentCode !== communityCode) return "rgba(35, 39, 52, 0.2)"
    else if (currentCode === communityCode) return "#252934"
    else return "#FEFEF9"
  }
  const hoveredCommunity = communities.find((community) => community.code === hoverCode)

  const onOpenCallsPage = router.pathname.includes("open-calls")
  const onCheckoutPage = router.pathname.includes("checkout")
  const onEventPage = router.pathname.includes("event")
  const onForumPage = router.pathname.includes("forum")
  const onLandingPage = router.pathname === "/c/[code]"
  const onNewsPage = router.pathname.includes("news")
  const onShopPage = router.pathname.includes("shop")
  const canSwitchCommunity = !!user && router.pathname !== "/c/[code]/checkout"

  const showOpenCalls = !isProductionEnvironment

  const getCommunityUrl = (communityCode: string, path: string) => {
    return COMMUNITIES_WITH_NEW_FUNCTIONALITY.includes(communityCode) && !isProductionEnvironment
      ? `${url}/${path}`
      : `${url}#${path}`
  }

  const getShopUrl = () => {
    return customizationData.styles.shopV2 ? `${url}/shop` : `${url}#shop`
  }

  if (!showContent) return null

  return (
    <div className="flex flex-row w-full flex-grow">
      <div className="flex flex-row w-full flex-grow">
        <div
          className={`flex ${
            isCheckoutPage(router.asPath) ? "w-0" : "max-w-0 sm:max-w-[272px] w-full"
          }`}
        >
          <div
            className={`hidden sm:flex h-screen sm:flex-col fixed top-0 left-0 w-[60px] bg-[#FEFEF9] sm:space-y-4 z-[46] ${
              onCheckoutPage
                ? "border-r-[1px] border-black border-opacity-[15%]"
                : communityStyles.navigation.sideBarSeparator
            } ${router.pathname === "/c/[code]/claim-card" ? "hidden" : ""}`}
          >
            <div className="flex flex-col flex-grow items-center relative w-auto py-0">
              <div
                className="h-[40px] flex items-center justify-center cursor-pointer"
                onClick={() => void router.push("/")}
              >
                <WERZLogoIcon height={11} width={40} />
              </div>
              {!onCheckoutPage ? (
                <Cart className="fixed top-5 right-5 font-mono text-right" color="#252934" />
              ) : null}
              <LogOut
                avatarId="avatar"
                className={`fixed bottom-4 ${user ? "cursor-pointer" : ""}`}
              />

              {!!user && (
                <div className="flex sm:flex-col items-center relative">
                  <div
                    className={`community-sidebar-list flex-col flex justify-start w-[60px] h-[87vh] hide-scroll overflow-y-auto overscroll-contain ${
                      isOverFlow
                        ? "border-b-[1px] border-t-[1px] border-black border-opacity-[15%]"
                        : ""
                    }`}
                    ref={containerRef}
                  >
                    {communities.map((c, i) => (
                      <div
                        className="text-white uppercase flex py-1 px-3 items-center space-y-1 cursor-pointer relative"
                        key={c.id}
                        onClick={() => void handleCommunityClick(c)}
                        onMouseEnter={(event) => {
                          void prefetchCommunityContent(c)
                          setHoverCode(c.code || "")
                          setHoverTooltipTop(event.currentTarget.offsetTop + 10)
                        }}
                        onMouseLeave={() => {
                          setHoverCode("")
                          setHoverTooltipTop(null)
                        }}
                      >
                        <PointIcon
                          className="absolute top-[23px] left-[-4px]"
                          color={detectColor(c.code || "")}
                        />
                        <MaskIcon
                          id={i.toString()}
                          src={communityIconUrl(c)}
                          strokeWidth={c.code === communityCode ? 2 : 1}
                          width={36}
                        />
                      </div>
                    ))}
                  </div>
                  {hoveredCommunity && !isOverFlow && hoverTooltipTop !== null ? (
                    <div
                      className="pointer-events-none absolute left-[55px] z-10 rounded-[4px] bg-[#252934] p-1 text-center font-mono text-[10px] uppercase text-white"
                      style={{ top: hoverTooltipTop }}
                    >
                      {hoveredCommunity.name}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <div className="wm-mobile-header-shell sm:hidden fixed inset-x-0 top-0 z-[31] border-b-[1px] bg-[#FEFEF9]">
            <div className="flex h-[50px] items-center justify-between w-full pl-3 pr-4">
              {headerCommunityName ? (
                <div
                  className={`flex gap-2 items-center justify-start w-[180px] ${
                    canSwitchCommunity ? "cursor-pointer" : ""
                  }`}
                  onClick={() => {
                    if (canSwitchCommunity) {
                      handleButtonClick(HEADER_ACTIONS.COMMUNITY_SWITCH)
                    }
                  }}
                >
                  <div className="flex-shrink-0">
                    <MaskIconV2 src={headerCommunityIconSrc} strokeWidth={2} width={24} />
                  </div>
                  <div className="uppercase leading-[18px] text-[18px] truncate font-mono">
                    {headerCommunityName}
                  </div>
                  {canSwitchCommunity ? (
                    <div className="flex-shrink-0">
                      <Triangle className={isSwitchCommunity ? "rotate-180" : ""} />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div />
              )}
              <div className="flex gap-3 items-center">
                {!onCheckoutPage ? (
                  <Cart
                    className="font-mono"
                    color={onCheckoutPage ? "#252934" : "#BDBEBE"}
                    onClick={() => handleButtonClick(HEADER_ACTIONS.CART)}
                  />
                ) : null}
                {router.pathname !== "/c/[code]/checkout" && (
                  <div
                    className="burger w-5 space-y-[3px]"
                    onClick={() => handleButtonClick(HEADER_ACTIONS.BURGER)}
                  >
                    <div
                      className={`h-[3px] transition-all transform rounded-[20px] ${
                        isMenuOpen ? "rotate-[45deg] bg-black translate-y-[6px]" : "bg-[#BDBEBE]"
                      }`}
                    />
                    <div
                      className={`h-[3px] transition-all rounded-[20px] ${
                        isMenuOpen ? "opacity-0" : "bg-[#BDBEBE]"
                      }`}
                    />
                    <div
                      className={`h-[3px] transition-all transform rounded-[20px] ${
                        isMenuOpen ? "rotate-[-45deg] bg-black -translate-y-[6px]" : "bg-[#BDBEBE]"
                      }`}
                    />
                  </div>
                )}
                <LogOut
                  avatarId="mobile-avatar"
                  className={`relative ${user ? "cursor-pointer" : ""}`}
                  onClick={() => handleButtonClick(HEADER_ACTIONS.LOGOUT)}
                />
              </div>
            </div>
          </div>

          <div
            className={`fixed inset-x-0 top-0 sm:left-[60px] sm:inset-x-auto sm:-top-2 flex flex-col sm:mt-2 sm:min-w-[200px] sm:max-w-[200px] z-[45] ${
              isCheckoutPage(router.asPath) ? "h-fit" : "sm:h-screen "
            } ${isMobileOverlayOpen ? "pointer-events-auto" : "pointer-events-none sm:pointer-events-auto"}`}
          >
            <div>
              {/*switch community menu*/}
              <div
                className={`wm-mobile-menu-panel h-[calc(100vh-50px)] h-[calc(100dvh-50px)] w-full overflow-y-auto overscroll-contain bg-[#FEFEF9] left-0 sm:-left-20 absolute top-[50px] transition-all transform flex justify-center p-5 pb-[calc(env(safe-area-inset-bottom)+20px)] ${
                  communityStyles.global.asideStyles
                } ${isSwitchCommunity ? "pointer-events-auto transform-none" : "pointer-events-none translate-y-[-200%]"}`}
              >
                <div className="flex justify-start items-center flex-wrap content-start gap-6 w-[338px]">
                  {communities?.map((community) => (
                    <div
                      className="w-[66px] h-[80px]"
                      id={community.id}
                      key={community.id}
                      onClick={() => {
                        void handleCommunityClick(community)
                      }}
                      onMouseEnter={() => void prefetchCommunityContent(community)}
                    >
                      <div
                        className="h-[65px] w-[68px] cursor-pointer flex justify-center items-center"
                        style={{
                          borderColor: community.code === communityCode ? "#1D1D42" : "#a5a5b3",
                        }}
                      >
                        <MaskIconV2 src={communityIconUrl(community)} strokeWidth={1} width={64} />
                      </div>
                      <p className="truncate text-center mb-2 mt-1 font-mono text-[9px] uppercase">
                        {community.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/*community menu*/}
              <div
                className={`wm-mobile-menu-panel h-[calc(100vh-44px)] sm:h-[100vh] text-center sm:text-left w-full sm:w-auto absolute sm:static top-[54px] justify-center left-0 sm:-left-20 flex-col px-20 sm:px-0 sm:pt-0 pt-[62px] sm:pb-0 pb-4 transition-all transform ${
                  communityStyles.global.asideStyles
                } ${
                  isMenuOpen
                    ? "pointer-events-auto transform-none"
                    : "pointer-events-none translate-y-[-200%] sm:pointer-events-auto sm:transform-none"
                } ${isCheckoutPage(router.asPath) ? "flex sm:hidden" : "flex"}`}
                style={{
                  background: communityStyles.global.asideBg,
                }}
              >
                <div className={`relative ${communityStyles.navigation.separator}`}>
                  <div className="flex justify-center">
                    <img
                      alt="community logo"
                      className={`${commonLogoProps.styles} cursor-pointer absolute bottom-[50px]`}
                      onClick={() => {
                        const community = communities.find((c) => c.code === communityCode)
                        if (community) {
                          void handleCommunityClick(community)
                        }
                      }}
                      src={commonLogoProps.src}
                    />
                  </div>
                </div>

                <Link
                  className={`${communityStyles.navigation.item} ${
                    onLandingPage ? communityStyles.navigation.itemSelected : ""
                  } sm:pl-4 py-3 sm:py-1 cursor-pointer`}
                  href={url}
                  onClick={closeMobileMenus}
                >
                  Landing
                </Link>

                {shouldHide(communityCode) ? null : (
                  <>
                    <div className={communityStyles.navigation.separator} />
                    <Link
                      className={`${communityStyles.navigation.item} ${
                        onNewsPage ? communityStyles.navigation.itemSelected : ""
                      } sm:pl-4 py-3 sm:py-1 cursor-pointer`}
                      href={getCommunityUrl(communityCode, "news")}
                      onClick={closeMobileMenus}
                    >
                      News
                    </Link>
                  </>
                )}

                <div className={communityStyles.navigation.separator} />
                <Link
                  className={`${communityStyles.navigation.item} ${
                    onEventPage ? communityStyles.navigation.itemSelected : ""
                  } block sm:pl-4 py-3 sm:py-1 cursor-pointer`}
                  href={getCommunityUrl(communityCode, "events")}
                  onClick={closeMobileMenus}
                >
                  Events
                </Link>

                {showOpenCalls && !shouldHide(communityCode) && communityStyles.openCalls ? (
                  <>
                    <div className={communityStyles.navigation.separator} />
                    <Link
                      className={`${communityStyles.navigation.item} ${
                        onOpenCallsPage ? communityStyles.navigation.itemSelected : ""
                      } block sm:pl-4 py-3 sm:py-1 cursor-pointer`}
                      href={getCommunityUrl(communityCode, "open-calls")}
                      onClick={closeMobileMenus}
                    >
                      Open Calls
                    </Link>
                  </>
                ) : null}

                {!shouldHide(communityCode) ? (
                  <>
                    {communityStyles.forumPosts &&
                    communityStyles.forumPost &&
                    !isProductionEnvironment ? (
                      <>
                        <div className={communityStyles.navigation.separator} />
                        <Link
                          className={`${communityStyles.navigation.item} ${
                            onForumPage ? communityStyles.navigation.itemSelected : ""
                          } block sm:pl-4 py-3 sm:py-1 cursor-pointer`}
                          href={`${url}/forum-posts`}
                          onClick={closeMobileMenus}
                        >
                          Forum
                        </Link>
                      </>
                    ) : null}

                    <div className={communityStyles.navigation.separator} />
                    <Link
                      className={`${communityStyles.navigation.item} ${
                        onShopPage ? communityStyles.navigation.itemSelected : ""
                      } block sm:pl-4 py-3 sm:py-1 cursor-pointe`}
                      href={getShopUrl()}
                      onClick={closeMobileMenus}
                    >
                      Shop
                    </Link>
                  </>
                ) : null}
                <div className={communityStyles.navigation.separator} />

                <div className="flex justify-center items-center w-full gap-4 absolute sm:bottom-4 bottom-6 -left-0">
                  {!!youtubeLink && (
                    <a
                      href={youtubeLink}
                      onClick={() => handleSocialClick("youtube", youtubeLink)}
                      rel="noopener"
                      target="_blank"
                    >
                      <YoutubeIcon fill={communityStyles.navigation.iconsColor} />
                    </a>
                  )}
                  {!!instagramLink && (
                    <a
                      href={instagramLink}
                      onClick={() => handleSocialClick("instagram", instagramLink)}
                      rel="noopener"
                      target="_blank"
                    >
                      <InstagramIcon fill={communityStyles.navigation.iconsColor} />
                    </a>
                  )}

                  {!!soundcloudLink && (
                    <a
                      href={soundcloudLink}
                      onClick={() => handleSocialClick("soundcloud", soundcloudLink)}
                      rel="noopener"
                      target="_blank"
                    >
                      <SoundCloudIcon fill={communityStyles.navigation.iconsColor} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {children}
      </div>
    </div>
  )
}
